import React, { useState, useEffect, useContext, memo } from 'react'
import { useInView } from 'react-intersection-observer'
import ReactDOM from 'react-dom'
import { AddToTrolley } from '@sainsburys-tech/bolt'
import { GridWrapper } from '@sainsburys-tech/grid'
import { ThemeContext } from 'styled-components'
import {
  StyledHeadingContainer,
  StyledContainer,
  StyledInstagramIcon,
  StyledImagesWrapper,
  StyledMiniButton,
  StyledLinkContainer,
  StyledLink,
  StyledChevron
} from './C002.styles'
import { isEmpty, agnostifyUrl } from '../../../helpers/utils'
import { fetcher } from '../../../helpers/ComponentHelper'
import {
  formatImages,
  createFilters,
  formatImagesStorystream,
  filterImageData,
  getTagsFromUrl,
  getImagesToRender
} from '../../functional/UGC/ugc.helpers.js'
import { CreateAlert } from '../../functional/UGC/UGC.presentational.jsx'
import {
  onPDPUGCLoaded,
  onPDPUGCViewed,
  onQuickViewSlidingPanelShut,
  onQuickViewArrowClicked
} from '../../functional/UGC/ugc.tracking'
import { C002Props, ImageDataType } from './types'
import C002Drawer from './C002Drawer'
import C002ImageItem from './C002ImageItem'
import C002Filters from './C002Filters'
import C002StorystreamDrawer from './C002StorystreamDrawer'
import { createGolDataAttributes } from '../../../helpers/tracking'

/**
 * C002 is a Carousel component that is to be used on PDP pages to show user generated content. It can be authored with an heading and bodyCopy the rest of the data comes from the curalate API.
 */

const C002 = ({
  heading = '',
  bodyCopy,
  layout = 'slider',
  limitMax = 50,
  productIdentifier,
  tags,
  streamID,
  isMini = false,
  link = {
    url: '',
    title: '',
    target: ''
  },
  ctaCopy = 'View More',
  showFilters = false,
  filters = []
}: C002Props): React.ReactElement => {
  const theme = useContext(ThemeContext)
  const { channel } = theme
  const isTags = tags?.length > 0
  const tagsString = isTags ? tags.toString() : ''
  const isSainsburys = channel === 'sainsburys'
  const isStack = layout === 'stack'
  const limitSlider = isSainsburys && !isMini ? 30 : 12
  const limitStack = isMini ? 8 : limitMax
  const initialLimit = isStack ? limitStack : limitSlider
  const hasLink = !!link.url

  const [imageData, setImageData] = useState<ImageDataType[]>([])
  const [selectedImage, setSelectedImage] = useState<ImageDataType>(null)
  const [loadMore, setLoadMore] = useState(true)
  const [totalPages, setTotalPages] = useState(0)
  const [sainsPage, setSainsPage] = useState(1)
  const [viewMoreButtonClicked, setViewMoreButtonClicked] = useState(false)
  const [limit, setLimit] = useState(initialLimit)
  const [selectedFilters, setSelectedFilters] = useState([])
  const [drawerInitialLoad, setDrawerInitialLoad] = useState(false)
  const [UGCStatus, setStatus] = useState({
    isError: false,
    isLoading: true,
    curalateCalled: false
  })

  // This is a flag if we want to use the tags for the PDP variant of the C002 to add extra images to the slider if needed
  const tagsFlag = !showFilters && !isSainsburys && isTags && UGCStatus.curalateCalled && imageData.length < 12

  const getGOLDataAttributes = (action?: string) =>
    createGolDataAttributes('isolated', 'C002 - NA', link?.title, link?.url, heading, action)

  const addGolTracking = (event: React.MouseEvent) => {
    const dataAttributes = getGOLDataAttributes()

    Object.keys(dataAttributes).forEach((attribute) =>
      event.currentTarget.setAttribute(attribute, dataAttributes[attribute])
    )
  }

  const ctaClick = (event) => {
    addGolTracking(event)
  }

  const DrawerVariant = isSainsburys ? C002StorystreamDrawer : C002Drawer

  const fetchUGCdata = async (
    hasTags: boolean,
    limitParam = isSainsburys ? sainsPage : limit,
    filtersArray = filters
  ) => {
    const filtersString = filtersArray?.length > 0 ? filtersArray.toString() : filters.toString()
    const sainsTagsParam = filtersArray.length > 0 ? `&tags=${filtersString}` : ''
    let apiUrl = ''
    const sainsburysAPI = 'https://d29qb9vav0xwuc.cloudfront.net/v4'
    const sainsburysAPIUrl = streamID
      ? `/sainsburys-pilot/${streamID}/search/live.json?access_token=54da843033ef26e29397dce44333b126def2035e&rpp=${initialLimit}&all_media=true&page=${limitParam}${sainsTagsParam}`
      : `/sainsburys-recipes/60530e337f509811a661d264/search/live/.json?access_token=00f96100723124c628144a4c338e945f80e7f015&rpp=${initialLimit}&all_media=true&page=${limitParam}${sainsTagsParam}`
    if (isSainsburys) {
      apiUrl = `${sainsburysAPI}${sainsburysAPIUrl}`
    } else {
      const productIdString = productIdentifier ? `productId:${productIdentifier}` : ''
      const filtersUrl = showFilters ? createFilters(filtersString) : productIdString
      apiUrl = `/global-api/ugc-curalate?&limit=${limitParam}${
        hasTags ? `&filter=${productIdString}or${createFilters(tagsString)}` : `&filter=${filtersUrl}`
      }`
    }

    try {
      const { json, ok, status } = await fetcher(apiUrl)
      if (!ok) {
        throw new Error(`There has been an error with (${status})`)
      }
      const filteredImageData = isSainsburys
        ? filterImageData(imageData, formatImagesStorystream(json.blocks), 'description')
        : filterImageData(imageData, formatImages(json.data.items), 'id')

      const taggedImageData = getImagesToRender(filteredImageData, filtersArray)
      const imageDataToUse = showFilters ? taggedImageData : filteredImageData
      return ReactDOM.unstable_batchedUpdates(() => {
        setImageData(imageDataToUse)
        if (isSainsburys) {
          setTotalPages(json.total_pages)
        }
        let showLoadMore
        if (isSainsburys) {
          showLoadMore = UGCStatus.curalateCalled ? limitParam < json.total_pages : true
        } else if (limitParam === limitMax) {
          showLoadMore = false
        } else {
          showLoadMore =
            imageDataToUse.length > limitMax || !showFilters
              ? limitParam <= limitMax
              : imageDataToUse.length >= limitParam
        }
        setLoadMore(showLoadMore)
        setStatus({
          isError: false,
          isLoading: false,
          curalateCalled: true
        })
        if (!hasTags) onPDPUGCLoaded(productIdentifier, window)
      })
    } catch (err) {
      console.error(err)
      return setStatus({
        isError: true,
        isLoading: false,
        curalateCalled: true
      })
    }
  }

  useEffect(() => {
    setSelectedFilters(getTagsFromUrl())
  }, [UGCStatus.curalateCalled])

  useEffect(() => {
    const filtersArr = showFilters ? selectedFilters : []
    fetchUGCdata(false, isSainsburys ? sainsPage : limit, filtersArr)
  }, [])

  useEffect(() => {
    if (!isSainsburys && isTags && UGCStatus.curalateCalled && imageData.length < 12) {
      fetchUGCdata(true)
    }
  }, [tags, UGCStatus.curalateCalled])

  const { ref, inView } = useInView({
    triggerOnce: true
  })

  useEffect(() => {
    if (inView) {
      onPDPUGCViewed(productIdentifier, window)
    }
  }, [inView, productIdentifier, onPDPUGCViewed])

  if ((!showFilters && !UGCStatus.isLoading && isEmpty(imageData)) || UGCStatus.isError) return null

  const imageItemOnClick = (item, index) => {
    setSelectedImage({ ...item, index })
    setDrawerInitialLoad(true)
  }

  const renderImages = (curalateLimit, sainsLimit) => {
    const imagesArray = []
    imageData
      .slice(0, isSainsburys ? sainsLimit : curalateLimit)
      .map((item: ImageDataType, index: number) =>
        imagesArray.push(
          <C002ImageItem
            key={`c002-image-item-${index}`}
            item={item}
            index={index}
            imageItemOnClick={imageItemOnClick}
            setSelectedImage={setSelectedImage}
            layout={layout}
          />
        )
      )

    return imagesArray
  }

  const isAlert = () => (showFilters && !UGCStatus.isLoading && isEmpty(imageData)) || UGCStatus.isError

  const handleUrlRedirect = () => {
    const hasPresetTag = link.url && link.url.indexOf('#') !== -1
    const linkSelectedTags = filters.length > 1 ? `${hasPresetTag ? ',' : '#'}${selectedFilters}` : ''
    const stripTrailingComma = (string) => string.replace(/(^,)/g, '')
    const ctaUrl = `${link.url}${stripTrailingComma(linkSelectedTags)}`

    window.open(agnostifyUrl(ctaUrl), link.target)
  }

  const stackCtaClick = (event: React.MouseEvent) => {
    if (hasLink) {
      addGolTracking(event)
      handleUrlRedirect()
    } else {
      setViewMoreButtonClicked(true)
      setLimit(limitMax)
      fetchUGCdata(tagsFlag, limitMax, selectedFilters)
    }
  }

  const sliderCtaClick = () => {
    if (!hasLink) {
      const newLimit = limit + 12 > limitMax ? limitMax : limit + 12
      const showLoadMore =
        imageData.length > limitMax || !showFilters ? newLimit <= limitMax : imageData.length > newLimit
      setLimit(newLimit)
      setLoadMore(showLoadMore)
      fetchUGCdata(tagsFlag, newLimit, selectedFilters)
    } else {
      handleUrlRedirect()
    }
  }

  const sainsCtaClick = (event: React.MouseEvent) => {
    if (hasLink) {
      addGolTracking(event)
      window.open(agnostifyUrl(link.url), link.target)
    } else {
      const newPage = sainsPage + 1
      const showLoadMore = sainsPage + 2 < totalPages
      setSainsPage(newPage)
      setLoadMore(showLoadMore)
      setViewMoreButtonClicked(true)
      fetchUGCdata(false, newPage, selectedFilters)
    }
  }

  const handleDrawerCarousel = (currentIndex: number, direction: string) => {
    let nextIndex: number

    if (direction === 'next') {
      nextIndex = currentIndex < imageData.length - 1 ? currentIndex + 1 : 0
    } else {
      nextIndex = currentIndex > 0 ? currentIndex - 1 : imageData.length - 1
    }

    onQuickViewArrowClicked(window, direction)
    setSelectedImage({ ...imageData[nextIndex], index: nextIndex })
    setDrawerInitialLoad(false)
    document.querySelector('[data-test=c002-product-container]').scrollTo({ top: 0, behavior: 'smooth' })
  }

  const Link = hasLink ? 'a' : StyledLink

  return (
    <AddToTrolley.Context>
      <AddToTrolley.Modal style={{ zIndex: 100 }} />
      <StyledContainer data-test='c002-container' {...getGOLDataAttributes('Load')} ref={ref}>
        {heading && (
          <StyledHeadingContainer>
            <h3 data-test='c002-heading' id='componentheading'>
              {heading}
            </h3>
            {!isSainsburys && <StyledInstagramIcon data-test='instagram-icon' />}
          </StyledHeadingContainer>
        )}
        {bodyCopy && <p data-test='c002-body-copy'>{bodyCopy}</p>}
        {showFilters && (
          <C002Filters
            filters={filters}
            selectedFilters={selectedFilters}
            setSelectedFilters={setSelectedFilters}
            setLoadMore={setLoadMore}
            sainsPage={sainsPage}
            totalPages={totalPages}
            limit={limit}
            fetchUGCdata={fetchUGCdata}
          />
        )}
        {isAlert() ? (
          CreateAlert(UGCStatus, imageData, channel)
        ) : (
          <>
            {isStack ? (
              <div>
                <GridWrapper cols={{ sm: 2, md: 3, lg: 4 }} testid='c002-grid'>
                  {renderImages(
                    !viewMoreButtonClicked ? limit : limitMax,
                    sainsPage === 1 && isMini ? 8 : initialLimit * sainsPage
                  )}
                </GridWrapper>
              </div>
            ) : (
              <StyledImagesWrapper data-test='c002-slider' role='list' aria-describedby='componentheading'>
                {renderImages(limit, initialLimit * sainsPage)}
                {isMini && loadMore && !UGCStatus.isLoading && (
                  <StyledLinkContainer
                    role={!hasLink ? 'button' : undefined}
                    onClick={isSainsburys ? sainsCtaClick : sliderCtaClick}
                    data-test='c002-mini-slider-cta-container'
                  >
                    <Link
                      data-test='c002-mini-slider-cta'
                      target={link?.target || undefined}
                      title={link?.title || undefined}
                      href={link?.url || undefined}
                      onClick={(evt) => !!link?.url && ctaClick(evt)}
                    >
                      {ctaCopy}
                      <StyledChevron data-test='c002-mini-slider-chevron' dir='right' />
                    </Link>
                  </StyledLinkContainer>
                )}
              </StyledImagesWrapper>
            )}
            {isStack && isMini && (isSainsburys ? loadMore : !viewMoreButtonClicked) && !UGCStatus.isLoading && (
              <StyledMiniButton
                data-test='c002-mini-stack-cta'
                kind='secondary'
                title={link?.title || undefined}
                target={link?.target || undefined}
                onClick={isSainsburys ? sainsCtaClick : stackCtaClick}
              >
                {ctaCopy}
              </StyledMiniButton>
            )}
            <DrawerVariant
              heading={heading || `Shared by @${selectedImage?.author}`}
              displayImage={selectedImage}
              toggleCallback={() => {
                setSelectedImage(null)
                onQuickViewSlidingPanelShut(window)
              }}
              carouselCallback={(currentIndex, direction) => handleDrawerCarousel(currentIndex, direction)}
              channel={channel}
              drawerInitialLoad={drawerInitialLoad}
            />
          </>
        )}
      </StyledContainer>
    </AddToTrolley.Context>
  )
}

export default memo(C002)
